import { useState } from "react"
import { Link, useNavigate } from "react-router"
import { useAuthStore } from "@/shared/hooks/use-auth-store"
import { useToast } from "@/client/components/common/toast"
import styles from "./register-page.module.css"

type RegisterStep = 1 | 2
type RegisterMethod = "phone" | "email"

// Back Icon SVG
const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
)

// Phone Icon SVG
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
  </svg>
)

// User Icon SVG
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
)

// Mail Icon SVG
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)

// Gift Icon SVG
const GiftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5"/>
    <line x1="12" y1="22" x2="12" y2="7"/>
    <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
  </svg>
)

// Sparkle Icon
const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z"/>
  </svg>
)

// Check Icon
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)

// Chevron Icon
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

export default function RegisterPage() {
  const navigate = useNavigate()
  const { signInWithPhone, signUpWithEmail, isLoading, error, clearError } = useAuthStore()
  const toast = useToast()

  const [step, setStep] = useState<RegisterStep>(1)
  const [method, setMethod] = useState<RegisterMethod>("phone")
  const [agreed, setAgreed] = useState(false)

  // Step 1: Basic Info
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [code, setCode] = useState("")
  const [countdown, setCountdown] = useState(0)

  // Step 2: Profile Info
  const [name, setName] = useState("")
  const [school, setSchool] = useState("")
  const [inviteCode, setInviteCode] = useState("")

  const startCountdown = () => {
    setCountdown(60)
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSendCode = async () => {
    if (countdown > 0 || phone.length !== 11) return
    clearError()
    try {
      const result = await signInWithPhone("+86" + phone)
      if (result.requiresOtp) {
        startCountdown()
        toast.success("验证码已发送")
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "发送验证码失败，请稍后重试")
    }
  }

  const handleNextStep = async () => {
    if (step === 1) {
      if (method === "phone") {
        // 手机注册：直接验证 OTP 完成注册
        clearError()
        try {
          await signInWithPhone("+86" + phone, code)
          toast.success("注册成功！")
          navigate("/me")
        } catch (err) {
          toast.error(err instanceof Error ? err.message : "注册失败，请重试")
        }
      } else {
        // 邮箱注册：进入 Step 2 填写昵称和学校
        setStep(2)
      }
    } else {
      // Step 2: 邮箱注册提交
      clearError()
      try {
        await signUpWithEmail(email, password, name, inviteCode, school)
        // 注册成功，提示用户去邮箱验证
        toast.success("注册成功！请前往邮箱验证您的账号")
        // 存储邮箱到 localStorage 供回调使用
        localStorage.setItem("pending_verification_email", email)
        navigate("/auth/callback")
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "注册失败，请重试")
      }
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
    } else {
      navigate(-1)
    }
  }

  const isStep1Valid = method === "phone"
    ? phone.length === 11 && code.length >= 4 && agreed
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && password.length >= 6 && agreed
  const isStep2Valid = name.length > 0 && school.length > 0

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={handleBack}>
          <BackIcon />
        </button>
        <div className={styles.headerTitle}>注册</div>
      </div>

      {/* Steps Indicator */}
      <div className={styles.steps}>
        <div className={`${styles.step} ${step >= 1 ? (step > 1 ? styles.completed : styles.active) : ""}`} />
        <div className={`${styles.step} ${step >= 2 ? styles.active : ""}`} />
      </div>

      {/* Hero Section */}
      <div className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <h1 className={styles.title}>
            {step === 1 ? "创建账号" : "完善资料"}
          </h1>
          <p className={styles.subtitle}>
            {step === 1 ? "加入蓝血精英，开启成长之旅" : "让更多人认识优秀的你"}
          </p>
        </div>
      </div>

      {/* Bonus Banner */}
      <div className={styles.bonusBanner}>
        <div className={styles.bonusIcon}>
          <SparkleIcon />
        </div>
        <div className={styles.bonusText}>
          <div className={styles.bonusTitle}>限时福利</div>
          <div className={styles.bonusDesc}>新用户注册即送 ¥100 体验金</div>
        </div>
      </div>

      {/* Referral Code */}
      <div className={styles.referralBanner}>
        <span className={styles.referralIcon}><GiftIcon /></span>
        <span className={styles.referralText}>输入邀请码双方额外获得 ¥50</span>
      </div>

      {/* Form Container */}
      <div className={styles.formContainer}>
        {step === 1 ? (
          // Step 1: Registration Method & Info
          <div className={styles.form}>
            {/* Method Tabs */}
            <div className={styles.methodTabs}>
              <button
                className={`${styles.methodTab} ${method === "phone" ? styles.activeTab : ""}`}
                onClick={() => setMethod("phone")}
              >
                <PhoneIcon />
                <span>手机注册</span>
              </button>
              <button
                className={`${styles.methodTab} ${method === "email" ? styles.activeTab : ""}`}
                onClick={() => setMethod("email")}
              >
                <MailIcon />
                <span>邮箱注册</span>
              </button>
            </div>

            {method === "phone" ? (
              // Phone Registration
              <>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>手机号</label>
                  <div className={styles.inputWrapper}>
                    <span className={styles.inputIcon}><PhoneIcon /></span>
                    <input
                      type="tel"
                      className={styles.input}
                      placeholder="请输入手机号"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      maxLength={11}
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>验证码</label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      className={`${styles.input} ${styles.inputWithBtn}`}
                      placeholder="请输入验证码"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      maxLength={6}
                    />
                    <button
                      className={styles.codeBtn}
                      onClick={handleSendCode}
                      disabled={countdown > 0 || phone.length !== 11}
                    >
                      {countdown > 0 ? `${countdown}s` : "获取验证码"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // Email Registration
              <>
                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>邮箱</label>
                  <div className={styles.inputWrapper}>
                    <span className={styles.inputIcon}><MailIcon /></span>
                    <input
                      type="email"
                      className={styles.input}
                      placeholder="请输入邮箱地址"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label className={styles.inputLabel}>设置密码</label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="password"
                      className={styles.input}
                      placeholder="请设置登录密码（至少6位）"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>邀请码（选填）</label>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="输入邀请码获得额外奖励"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  maxLength={8}
                />
              </div>
            </div>

            <div className={styles.checkbox}>
              <input
                type="checkbox"
                id="agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label htmlFor="agree">
                我已阅读并同意 <a href="#terms">《用户协议》</a> 和 <a href="#privacy">《隐私政策》</a>
              </label>
            </div>

            <button
              className={styles.registerBtn}
              onClick={handleNextStep}
              disabled={!isStep1Valid || isLoading}
            >
              {isLoading ? "处理中..." : method === "phone" ? "注册" : "下一步"}
            </button>
          </div>
        ) : (
          // Step 2: Profile Info
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>姓名</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}><UserIcon /></span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="请输入您的姓名"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>学校</label>
              <div className={styles.inputWrapper}>
                <span className={styles.inputIcon}><MailIcon /></span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="请输入您的学校名称"
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                />
              </div>
            </div>

            <button
              className={styles.registerBtn}
              onClick={handleNextStep}
              disabled={!isStep2Valid || isLoading}
            >
              {isLoading ? "注册中..." : "完成注册"}
            </button>

            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>或</span>
              <span className={styles.dividerLine} />
            </div>

            <div className={styles.socialBtns}>
              <button className={styles.socialBtn}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"/>
                </svg>
              </button>
              <button className={styles.socialBtn}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z"/>
                </svg>
              </button>
              <button className={styles.socialBtn}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        已有账号？
        <Link to="/login" className={styles.footerLink}>立即登录</Link>
      </div>
    </div>
  )
}